import { Network } from 'lucide-react';
import type { ConnectedHost } from '@simpleacs/shared';
import { Card, EmptyState, Pill, Table, TBody, THead, TRow } from '../../components/ui';
import { RefreshButton } from '../../components/RefreshButton';
import { formatDateTime } from '../../lib/format';
import { macTableRows, macTableSummary, type LearnedMacRow } from '../../lib/mac-table';

/**
 * The MAC table: every address seen behind this ONU, from the OLT (what it
 * learned on the ONU's VLANs) and from the router (the hosts it reports),
 * joined by MAC. On a bridged unit the router sees nothing — traffic goes
 * straight through — so the OLT's side is the whole answer there; on a
 * routed unit the OLT sees only the router's own WAN interfaces and the
 * router's host table is the subscriber's devices. Both are shown, each
 * labelled with who said it (operator, 2026-10-16: "add a mac table tab").
 */
export function MacTableTab({
  macs,
  hosts,
  fetchedAt,
  cached,
  unread,
  loading,
  refresh,
  onNotice,
}: {
  macs: LearnedMacRow[];
  hosts: ConnectedHost[];
  /** When the OLT side was read (ms), null when never. */
  fetchedAt: number | null;
  /** The OLT side came from the snapshot, not a fresh read. */
  cached: boolean;
  /** The OLT has never been read for this ONU. */
  unread: boolean;
  loading: boolean;
  /** ONE refresh for the whole table: the OLT's read and, where there is a router, its host table (operator, 2026-10-16: two buttons read as a mistake). */
  refresh: () => Promise<unknown>;
  onNotice: (message: string, tone?: 'info' | 'danger') => void;
}) {
  const rows = macTableRows(macs, hosts);
  const when = fetchedAt ? formatDateTime(fetchedAt) : null;
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Network className="size-4 text-muted" />
          MAC table
          <span className="text-xs font-normal text-faint">{rows.length}</span>
        </span>
      }
      action={<RefreshButton label="the MAC table" run={refresh} invalidate={[['device']]} onNotice={onNotice} />}
    >
      <p className="mb-3 text-xs text-muted">
        {macTableSummary(rows)}
        {when ? ` · OLT side read ${when}${cached ? ' (from the last read)' : ''}` : unread ? ' · the OLT has not been read yet' : ''}
      </p>
      {loading && rows.length === 0 ? (
        <span className="text-xs text-faint">Reading the OLT…</span>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No MAC address learned"
          description="The OLT has learned no address behind this ONU and the router reports no host. Nothing is connected, or nothing has sent a frame since the OLT last aged its table."
        />
      ) : (
        <Table>
          <THead>
            <th className="th">MAC</th>
            <th className="th">Device</th>
            <th className="th">VLAN</th>
            <th className="th">Seen on</th>
            <th className="th">Router</th>
            <th className="th">Seen by</th>
          </THead>
          <TBody>
            {rows.map((r) => (
              <TRow key={r.mac}>
                <td className="td font-mono text-xs">{r.mac}</td>
                <td className="td">
                  {r.device ? r.device : r.randomised ? <span className="text-faint">private address</span> : <span className="text-faint">unknown</span>}
                </td>
                <td className="td text-muted">{r.vlanId ?? (r.seenBy === 'router' ? '—' : 'untagged')}</td>
                <td className="td text-muted">{r.ontPort ?? '—'}</td>
                <td className="td text-muted">
                  {r.host ? (
                    <span>
                      {r.host.ip ?? 'no address'}
                      {r.host.iface ? ` · ${r.host.iface}` : ''}
                      {r.host.active ? '' : ' · inactive'}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="td">
                  <Pill tone={r.seenBy === 'both' ? 'primary' : 'neutral'}>
                    {r.seenBy === 'both' ? 'OLT and router' : r.seenBy === 'olt' ? 'OLT' : 'router'}
                  </Pill>
                </td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}
